// src/scenes/items/CreateItem.jsx
import { useEffect, useMemo, useState, useCallback } from "react";
import * as yup from "yup";
import axios from "axios";
import Header from "../../components/Header";
import SimpleForm from "../../components/SimpleForm";
import { Snackbar, Alert, CircularProgress, Box } from "@mui/material";

/* ---------- axios ---------- */
const token = localStorage.getItem("token");
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 15000,
  headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
});

/* ---------- helpers ---------- */
const buildInitials = (fields) =>
  fields.reduce((acc, f) => {
    const def =
      f.default ??
      (f.type === "checkbox"
        ? false
        : f.type === "number"
        ? ""
        : f.type === "date"
        ? ""
        : "");
    acc[f.name] = def;
    return acc;
  }, {});

const buildSchema = (fields) => {
  const shape = {};
  fields.forEach((f) => {
    let rule;
    switch (f.type) {
      case "number":
        rule = yup.number().typeError("must be a number");
        break;
      case "checkbox":
        rule = yup.boolean();
        break;
      case "date":
        rule = yup.date().typeError("invalid date");
        break;
      case "select":
        // For selects with numeric IDs, we still receive string from TextField.
        // Keep as string here; we'll coerce to number on submit.
        rule = yup.string();
        break;
      default:
        rule = yup.string();
    }
    if (f.required) rule = rule.required("required");
    if (f.min != null) rule = rule.min(f.min);
    if (f.max != null) rule = rule.max(f.max);
    if (f.matches) rule = rule.matches(f.matches.regex, f.matches.message);
    shape[f.name] = rule;
  });
  return yup.object().shape(shape);
};

/* ---------- POST helper ---------- */
async function postJSON(path, data) {
  try {
    const res = await api.post(path, data);
    return { data: res.data, error: null };
  } catch (err) {
    const serverErrors =
      err?.response?.data?.errors ||
      err?.response?.data?.message ||
      err.message;
    return { data: null, error: serverErrors };
  }
}

/* ---------- constants ---------- */
const UNIT_OPTIONS = [
  "bottle",
  "pack",
  "box",
  "piece",
  "kg",
  "g",
  "dozen",
  "crate",
  "carton",
  "bag",
].map((u) => ({ value: u, label: u }));

/* ---------- page ---------- */
export default function CreateItem() {
  const [toast, setToast] = useState({ open: false, msg: "", sev: "success" });
  const [loadingOpts, setLoadingOpts] = useState(true);
  const [catOptions, setCatOptions] = useState([]);
  const [lastRefreshed, setLastRefreshed] = useState(Date.now());

  // Fetch category dropdown data (callable + cache-busting)
  const loadOptions = useCallback(async () => {
    setLoadingOpts(true);
    const t = Date.now();
    try {
      const catsRes = await api.get("/api/category", {
        params: { t },
        
      });

      const catOpts = (catsRes?.data?.data ?? [])
        .sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || ""))
        )
        .map((c) => ({ value: String(c.id), label: c.name }));

      setCatOptions(catOpts);
      setLastRefreshed(Date.now()); // force remount of form/fields when options change
    } catch (e) {
      setToast({
        open: true,
        msg: "Failed to load categories",
        sev: "error",
      });
    } finally {
      setLoadingOpts(false);
    }
  }, []);

  // Load once on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!cancelled) await loadOptions();
    })();
    return () => {
      cancelled = true;
    };
  }, [loadOptions]);

  // Refresh when the window regains focus (navigate back to this tab)
  useEffect(() => {
    const onFocus = () => loadOptions();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadOptions]);

  // Optional: allow other screens to trigger refresh
  useEffect(() => {
    const handler = () => loadOptions();
    window.addEventListener("items:refresh-master-data", handler);
    return () =>
      window.removeEventListener("items:refresh-master-data", handler);
  }, [loadOptions]);

  const FIELDS = useMemo(() => {
    return [
      {
        name: "categoryId",
        label: "Category",
        type: "select",
        span: 2,
        required: true,
        options: catOptions,
        key: `cat-${lastRefreshed}`,
        props: {
          disabled: loadingOpts || catOptions.length === 0,
          placeholder: loadingOpts ? "Loading..." : "Choose category",
        },
      },
      {
        name: "name",
        label: "Item Name",
        type: "text",
        span: 2,
        required: true,
        props: { placeholder: "e.g. Habesha Beer 330ml" },
      },
      {
        name: "unit",
        label: "Unit",
        type: "select",
        span: 1,
        required: true,
        options: UNIT_OPTIONS,
      },
      
      {
        name: "salePrice",
        label: "Sale Price",
        type: "number",
        span: 1,
        required: true,
        min: 0,
        props: {
    placeholder: "Enter amount",
    inputProps: { step: 0.01, min: 0 },
    onKeyDown: (e) => {
      if (e.key === "-" || e.key === "e") {
        e.preventDefault();
      }
    }
  }
      },
      {
        name: "minQuantity",
        label: "Minimum Quantity",
        type: "number",
        span: 1,
        required: true,
        min: 0,
        props: {
    placeholder: "Enter amount",
    inputProps: { step: 0.01, min: 0 },
    onKeyDown: (e) => {
      if (e.key === "-" || e.key === "e") {
        e.preventDefault();
      }
    }
  }
      },
       

      {
        name: "expirationDate",
        label: "Expiration Date",
        type: "date",
        span: 1,
        required: false,
        props: { InputLabelProps: { shrink: true } },
      },
      {
        name: "description",
        label: "Description",
        type: "textarea",
        span: 4,
        required: false,
        props: { placeholder: "Optional details" },
      },
    ];
  }, [catOptions, loadingOpts, lastRefreshed]);

  const INITIALS = useMemo(() => buildInitials(FIELDS), [FIELDS]);
  const SCHEMA = useMemo(() => buildSchema(FIELDS), [FIELDS]);

  const handleSubmit = async (
    values,
    { setSubmitting, setErrors, resetForm }
  ) => {
    // Coerce numeric IDs from select (come as strings)
    const payload = {
      ...values,
      categoryId: values.categoryId ? Number(values.categoryId) : undefined,
      salePrice: Number(values.salePrice),
      minQuantity: Number(values.minQuantity),
      
    };

    // Ensure ISO string for expirationDate if present
    if (values.expirationDate) {
      const d = new Date(values.expirationDate);
      if (!isNaN(d.getTime()))
        payload.expirationDate = d.toISOString().slice(0, 10); // send YYYY-MM-DD if backend expects date-only
    } else {
      delete payload.expirationDate;
    }

    const { data, error } = await postJSON("/api/item", payload);
    setSubmitting(false);

    if (error) {
      if (typeof error === "object" && !Array.isArray(error)) setErrors(error);
      setToast({ open: true, msg: "Failed to create item", sev: "error" });
      return;
    }

    const msg = data?.message || "Item created successfully";
    setToast({ open: true, msg, sev: "success" });
    resetForm();

    // Refresh categories after successful create
    await loadOptions();
  };

  const LoadingBar = loadingOpts ? (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
      <CircularProgress size={18} />
      <span style={{ fontSize: 14, opacity: 0.8 }}>Loading categories…</span>
    </Box>
  ) : null;

  // Force SimpleForm to re-render when options change, even if it memoizes internally
  const formKey = `${lastRefreshed}`;

  return (
    <>
      <SimpleForm
        key={formKey}
        title="CREATE ITEM"
        subtitle="Add a new inventory item"
        Header={Header}
        fields={FIELDS}
        initialValues={INITIALS}
        validation={SCHEMA}
        onSubmit={handleSubmit}
        submitLabel="Create Item"
        columns={4}
      />

      {LoadingBar}

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 5 }}
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setToast((s) => ({ ...s, open: false }))}
          severity={toast.sev}
          variant="filled"
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
